/**
 * Post-setup token self-heal.
 *
 * The setup SPA (`dashboard.{root}/setup`) shares the dashboard's Auth0 client
 * and `localStorage` token cache, and force-refreshes the token before it hands
 * off here — so the dashboard normally lands on the fresh, post-setup token
 * (with the user's real org roles). But that refresh is best-effort (it swallows
 * errors) and can race DynamoDB propagation, so the dashboard can occasionally
 * boot on a STALE "setup bootstrap" token that carries only `initial-setup:*`
 * scopes. Rendering the app with those scopes shows an empty, permission-denied
 * dashboard.
 *
 * This guard closes that gap with a single, loop-safe self-heal keyed off the
 * unambiguous setup signal (the synthetic `<env>-SetupBootstrap` role the
 * post-login Action grants ONLY while `app_metadata.setup_completed !== true`):
 *
 *   1. First time we see a bootstrap-only token for this user → force ONE hard
 *      token refresh (`cacheMode: "off"`, re-running the post-login Action) and
 *      reload, so every consumer re-reads the elevated claims. This recovers the
 *      stale-cache / propagation-lag case.
 *   2. Still bootstrap-only after that refresh → the user genuinely hasn't
 *      finished setup, so send them ONCE to the setup SPA to complete it
 *      (carrying a `returnTo` back to where they were headed).
 *   3. Back on the dashboard STILL bootstrap-only after both (1) and (2) → the
 *      setup site bounced us straight back (it can misread an unfinished account
 *      as done until the sticky-`flow_type` Action fix ships). Stop here and
 *      render rather than ping-ponging: a limited dashboard beats an infinite
 *      redirect loop.
 *
 * Two per-user `sessionStorage` flags cap it at exactly one refresh + one
 * redirect, so it can NEVER loop. When the token carries real roles both flags
 * are cleared and children render normally.
 */
import * as React from "react";
import { useSession } from "@authe/ui-core/session";
import { resolveAppUrl } from "@authe/fe-commons/auth";
import { APP_NAME, urlContext } from "@/config";

/** Registry slug for the onboarding SPA (aliasOf `dashboard`, mounted at /setup). */
const SETUP_APP = "setup";
const REFRESH_FLAG_PREFIX = "dashboard.postSetupRefresh.";
const REDIRECT_FLAG_PREFIX = "dashboard.postSetupRedirect.";

function readFlag(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeFlag(key: string): void {
  try {
    sessionStorage.setItem(key, "1");
  } catch {
    /* storage disabled */
  }
}

function clearFlags(keys: string[]): void {
  try {
    for (const k of keys) sessionStorage.removeItem(k);
  } catch {
    /* storage disabled */
  }
}

/** True when the token's ONLY grant is the setup-bootstrap role (any env). */
export function isBootstrapOnlyToken(roles: unknown): boolean {
  if (!Array.isArray(roles) || roles.length === 0) return false;
  return roles.every((r) => typeof r === "string" && r.endsWith("-SetupBootstrap"));
}

function FinishingSetup(): React.ReactElement {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3 bg-background text-foreground">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
      <p className="text-sm text-muted-foreground">Finishing setup for {APP_NAME}…</p>
    </div>
  );
}

export function PostSetupTokenGuard({ children }: { children: React.ReactNode }): React.ReactElement {
  const { session, manager } = useSession();
  const roles = session?.claims.roles;
  const bootstrapOnly = isBootstrapOnlyToken(roles);

  React.useEffect(() => {
    if (!session) return;
    const userId = session.user.id ?? "anon";
    const refreshKey = `${REFRESH_FLAG_PREFIX}${userId}`;
    const redirectKey = `${REDIRECT_FLAG_PREFIX}${userId}`;

    // Healthy token (has real roles) — clear the one-shot flags so a future
    // hand-off can self-heal again, and let the app render.
    if (!bootstrapOnly) {
      clearFlags([refreshKey, redirectKey]);
      return;
    }

    // (1) First bootstrap-only boot for this user: the cache may be a stale
    // pre-setup token. Force a hard re-mint (re-runs the post-login Action) and
    // reload so all consumers re-read the elevated claims.
    if (!readFlag(refreshKey)) {
      writeFlag(refreshKey);
      void (async () => {
        try {
          await manager.refreshAccessToken();
        } catch {
          /* refresh unavailable → fall through to a reload; if it's still
             bootstrap-only the next pass redirects to setup */
        }
        window.location.reload();
      })();
      return;
    }

    // (2) Still bootstrap-only after a hard refresh → setup is genuinely
    // unfinished. Send them ONCE to the onboarding SPA to complete it.
    if (!readFlag(redirectKey)) {
      writeFlag(redirectKey);
      const setupUrl = resolveAppUrl({ application: SETUP_APP, path: "/" }, urlContext);
      try {
        const url = new URL(setupUrl);
        url.searchParams.set("returnTo", window.location.href);
        window.location.assign(url.toString());
      } catch {
        window.location.assign(setupUrl);
      }
      return;
    }

    // (3) Refreshed AND redirected, still bootstrap-only → the setup site bounced
    // us back (it read the unfinished account as done). Do NOT loop: fall through
    // and render so the user gets a (limited) dashboard instead of a redirect
    // storm. Resolves fully once the sticky-flow_type Action fix ships.
  }, [session, bootstrapOnly, manager]);

  // While the FIRST refresh/redirect is still pending, don't paint the app with
  // setup-only scopes. Once we've exhausted both self-heal steps (loop-break),
  // render children rather than hanging on the spinner forever.
  const userId = session?.user.id ?? "anon";
  const exhausted = readFlag(`${REFRESH_FLAG_PREFIX}${userId}`) && readFlag(`${REDIRECT_FLAG_PREFIX}${userId}`);
  if (session && bootstrapOnly && !exhausted) return <FinishingSetup />;
  return <>{children}</>;
}
